// Team bios — the comms-facing bio of a real team member.
//
// The section this backs used to be a list of free-typed names in
// comms.founders. Nothing tied those to the app's employees, so the list drifted
// the moment somebody was renamed or left. Here the member is picked from
// public.users, the portrait and profile intro are shown exactly as the team
// member's own page shows them, and the bio written beside them is stored
// against that user's id (comms.team_bios).
import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import { commsUpsert } from './commsUtils';
import { S, BlockTitle, FilterBtn, NoteArea, EmptyNote } from './commsUi';

// ── Selection rules (pure, tested) ─────────────────────────────────────────

// Logins that are not people. Kept in step with SYSTEM_ACCOUNT_EMAILS /
// SYSTEM_ACCOUNT_IDS in supabase/functions/_shared/teamPortraits.ts, which
// excludes the same accounts from the Dropbox portrait tree. Matched by exact
// address rather than by pattern: admin@architecturebrio.com is a real member.
export const SYSTEM_ACCOUNT_EMAILS = new Set([
  'info@architecturebrio.com',
  'press@architecturebrio.com',
  'studio@architecturebrio.com',
]);
export const SYSTEM_ACCOUNT_IDS = new Set([1]);

export function isSystemAccount(user) {
  if (SYSTEM_ACCOUNT_IDS.has(Number(user.id))) return true;
  return SYSTEM_ACCOUNT_EMAILS.has(String(user.email || '').trim().toLowerCase());
}

// is_active is nullable and the app reads a null as active (`is_active !==
// false` throughout useSupabase.js), so the same reading is used here — and by
// bucketFor() in teamPortraits.ts, which is what decides whether a member's
// portrait folder sits under Active/ or Non-Active/.
export function isActiveMember(user) {
  return user.is_active !== false;
}

/**
 * The members offered in the dropdown for one side of the toggle.
 * `bucket` is 'active' | 'non-active'.
 */
export function membersInBucket(users, bucket) {
  const wantActive = bucket !== 'non-active';
  return (users || [])
    .filter(u => !u.deleted_at)
    .filter(u => !isSystemAccount(u))
    .filter(u => String(u.name || '').trim())
    .filter(u => isActiveMember(u) === wantActive)
    .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }));
}

// ── Portrait ───────────────────────────────────────────────────────────────

// The photo is a storage path, so it has to be signed before it can be shown,
// and the signing is a round trip. The resolved URL therefore carries the path
// it was signed for: without that, switching members leaves the previous
// member's face under the new member's name until the new URL lands, and a slow
// response for the member you just left would overwrite the one you are on.
function useSignedPortrait(storagePath) {
  const [resolved, setResolved] = useState({ path: null, url: null });

  useEffect(() => {
    if (!storagePath) { setResolved({ path: null, url: null }); return; }
    if (storagePath.startsWith('data:')) { setResolved({ path: storagePath, url: storagePath }); return; }
    let cancelled = false;
    supabase.storage.from('profile-photos').createSignedUrl(storagePath, 3600)
      .then(res => { if (!cancelled) setResolved({ path: storagePath, url: res.data ? res.data.signedUrl : null }); })
      .catch(() => { if (!cancelled) setResolved({ path: storagePath, url: null }); });
    return () => { cancelled = true; };
  }, [storagePath]);

  return resolved.path === storagePath ? resolved.url : null;
}

const PORTRAIT_W = 190;

function Portrait({ url, name, loading }) {
  const box = {
    width: PORTRAIT_W, aspectRatio: '4 / 5', flexShrink: 0, borderRadius: 3,
    backgroundColor: '#E9E6DF', border: '1px solid rgba(27,27,25,.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  };
  if (url) {
    return <img src={url} alt={name ? `${name} — portrait` : 'Portrait'} style={{ ...box, objectFit: 'cover', objectPosition: '50% 25%', display: 'block' }} />;
  }
  return (
    <div style={box}>
      <span style={{ ...S.eyebrow, fontSize: 10, textAlign: 'center', padding: '0 10px' }}>
        {loading ? 'Loading…' : 'No portrait on file'}
      </span>
    </div>
  );
}

// ── Section ────────────────────────────────────────────────────────────────

/**
 * @param users     public.users rows: id, name, email, job_role, is_active,
 *                  office_location, deleted_at, profile_intro
 * @param profiles  public.user_profiles rows: user_id, profile_intro,
 *                  profile_photo_rect, designation, current_job_role
 * @param bios      comms.team_bios rows: user_id, bio
 */
export function TeamBiosSection({ users = [], profiles = [], bios = [], canEdit, showToast }) {
  const [bucket, setBucket] = useState('active');
  const [selectedId, setSelectedId] = useState('');
  // Bios written in this session, so the field keeps what was typed without
  // waiting for the whole practice tab to refetch. Keyed by user id.
  const [edited, setEdited] = useState({});
  const [saving, setSaving] = useState(false);

  const active = useMemo(() => membersInBucket(users, 'active'), [users]);
  const nonActive = useMemo(() => membersInBucket(users, 'non-active'), [users]);
  const list = bucket === 'non-active' ? nonActive : active;

  const profileByUser = useMemo(() => {
    const m = new Map();
    (profiles || []).forEach(p => m.set(Number(p.user_id), p));
    return m;
  }, [profiles]);

  const bioByUser = useMemo(() => {
    const m = new Map();
    (bios || []).forEach(b => m.set(Number(b.user_id), b.bio || ''));
    return m;
  }, [bios]);

  // A member selected on one side of the toggle is not in the list on the
  // other, so switching clears the selection rather than leaving a name
  // showing that the dropdown no longer offers.
  useEffect(() => {
    if (selectedId && !list.some(u => String(u.id) === String(selectedId))) setSelectedId('');
  }, [list, selectedId]);

  const selected = list.find(u => String(u.id) === String(selectedId)) || null;
  const profile = selected ? profileByUser.get(Number(selected.id)) : null;
  const photoPath = (profile && profile.profile_photo_rect) || '';
  const portraitUrl = useSignedPortrait(photoPath);
  const intro = (profile && profile.profile_intro) || (selected && selected.profile_intro) || '';
  const role = (profile && (profile.designation || profile.current_job_role)) || (selected && selected.job_role) || '';

  const storedBio = selected ? (bioByUser.get(Number(selected.id)) || '') : '';
  const currentBio = selected && edited[selected.id] !== undefined ? edited[selected.id] : storedBio;

  // The save is fired on blur, and a member can be switched while one is in
  // flight, so the id being saved is captured rather than read from state.
  const saveBio = async (userId, text) => {
    if (text === (bioByUser.get(Number(userId)) || '')) return;
    setSaving(true);
    try {
      await commsUpsert('team_bios', { user_id: Number(userId), bio: text, updated_at: new Date().toISOString() }, 'user_id');
      setEdited(e => ({ ...e, [userId]: text }));
      showToast('Bio saved');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const withBioCount = useMemo(() => {
    const ids = new Set(list.map(u => Number(u.id)));
    let n = 0;
    ids.forEach(id => {
      const text = edited[id] !== undefined ? edited[id] : (bioByUser.get(id) || '');
      if (String(text).trim()) n += 1;
    });
    return n;
  }, [list, bioByUser, edited]);

  return (
    <div style={S.card}>
      <BlockTitle
        left="Team bios"
        right={`${withBioCount} of ${list.length} written`}
      />

      <div style={{ ...S.filterRow, marginBottom: 12 }}>
        <FilterBtn label={`Active · ${active.length}`} active={bucket === 'active'} onClick={() => setBucket('active')} />
        <FilterBtn label={`Non-active · ${nonActive.length}`} active={bucket === 'non-active'} onClick={() => setBucket('non-active')} />
      </div>

      <select
        value={selectedId}
        onChange={e => setSelectedId(e.target.value)}
        aria-label="Team member"
        style={{ ...S.inputStyle, maxWidth: 340, marginBottom: 16 }}
      >
        <option value="">— select a team member —</option>
        {list.map(u => (
          <option key={u.id} value={u.id}>
            {u.name}{u.job_role ? ` — ${u.job_role}` : ''}
          </option>
        ))}
      </select>

      {list.length === 0 && <EmptyNote text={bucket === 'non-active' ? 'No non-active members.' : 'No active members.'} />}

      {!selected && list.length > 0 && (
        <EmptyNote text="Pick a team member to see their portrait and profile intro, and to write their comms bio." />
      )}

      {selected && (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Portrait url={portraitUrl} name={selected.name} loading={!!photoPath && !portraitUrl} />

          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            <h3 style={{ ...S.h3, marginBottom: 2 }}>{selected.name}</h3>
            <p style={{ ...S.meta, marginBottom: 14 }}>
              {[role, selected.office_location].filter(Boolean).join(' · ')}
              {selected.is_active === false ? ' · non-active' : ''}
            </p>

            <div style={{ ...S.eyebrow, marginBottom: 6 }}>Profile intro</div>
            {intro
              ? <p style={{ ...S.body, marginBottom: 18, whiteSpace: 'pre-wrap' }}>{intro}</p>
              : <p style={{ ...S.body, marginBottom: 18, fontStyle: 'italic' }}>No profile intro on their team page yet.</p>}

            <div style={{ ...S.eyebrow, marginBottom: 6, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span>Comms bio</span>
              <span style={{ textTransform: 'none', letterSpacing: 0 }}>{saving ? 'Saving…' : 'saved on blur'}</span>
            </div>
            {canEdit ? (
              <NoteArea
                key={selected.id}
                value={currentBio}
                onChange={v => saveBio(selected.id, v)}
                saveOnBlur
                placeholder="Rewrite the intro as a press bio — third person, present tense…"
                minHeight={140}
              />
            ) : (
              currentBio
                ? <p style={{ ...S.body, whiteSpace: 'pre-wrap' }}>{currentBio}</p>
                : <EmptyNote text="No comms bio written yet." />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamBiosSection;
